/**
 * The bill of materials beside six PLM products: Arena, Windchill, Teamcenter, Agile PLM, Duro
 * and OpenBOM. Ed asked for it on 2026-10-31: "compare our BOM to the competitors".
 *
 * ══ THE RULES THIS FILE IS WRITTEN UNDER ══ (the same ones t4's matrix is written under)
 *
 * EVERY CELL IS SOURCED. Desk research on 2026-10-31 against vendor help centres where they
 * exist, with one URL per claim recorded in docs/objects/competitor-bom-research.md. Nobody's
 * product was signed into and nobody's code was read. Two columns are weaker than the rest and
 * the page says so: Arena's end-user help centre no longer resolves, so its column rests on its
 * API documentation and marketing; Siemens' Teamcenter help is not web-indexed, so two of its
 * cells rest on a third-party training site.
 *
 * "NOT ESTABLISHED" IS NOT "NO". A matrix that turned every gap in our reading into a
 * competitor's missing feature would be wrong on the facts and indefensible if quoted back.
 *
 * OUR OWN COLUMN CARRIES EVERY NO WE HAVE: no comparison between revisions, no spreadsheet or
 * CAD import, no line-level alternates, no manufacturer parts, no line writes on the public
 * API. The rows we lose are what make the rows we win credible. One No is a decision rather
 * than a gap and is marked as such: the bill is captured on the item and controlled by a change
 * order, not frozen into the item's revision.
 *
 * KEEP IT IN STEP WITH THE CATALOGUE. Where a row describes something a capability card covers,
 * the two must agree about whether we have it; featureMatrix.test.ts checks what it can -- that
 * every cell carries a level and that our column never claims a Yes no built card stands behind.
 */

export type Level =
    /** The product does this. */
    | "yes"
    /** It does part of it, or does it in a way that leaves the point of the row unmet. */
    | "partial"
    /** It does not, and that was established rather than assumed. */
    | "no"
    /** The reading did not establish it either way. NOT a polite "no". */
    | "unknown";

export const LEVEL_LABEL: Record<Level, string> = {
    yes: "Yes",
    partial: "Partly",
    no: "No",
    unknown: "Not established",
};

export type Cell = {
    level: Level;
    note?: string;
    /** On OUR cell only: a no we chose, not a no we failed at. */
    stance?: "declined";
};

export type Contest = "contested" | "ceded" | "behind" | "ahead" | "neither";

export const CONTEST_LABEL: Record<Contest, string> = {
    contested: "Head to head",
    ceded: "Ceded on purpose",
    behind: "Behind",
    ahead: "Ahead",
    neither: "Neither",
};

/** The six products, in the order their columns are drawn; ours is drawn last, so a reader
 *  arrives at it having read what the others do. */
export const PRODUCTS = ["Arena", "Windchill", "Teamcenter", "Agile PLM", "Duro", "OpenBOM"] as const;
export type Product = typeof PRODUCTS[number];

export type MatrixRow = {
    capability: string;
    /** What the row is asking, where the name alone could be read two ways. */
    note?: string;
    them: Record<Product, Cell>;
    ours: Cell;
};

const ANSWERS = (cell: Cell) => cell.level === "yes" || cell.level === "partial";

/** How a row sits against the field, derived rather than typed. "The field" answers when ANY of
 *  the six does: a reader judging where to compete wants to know whether somebody has it, not
 *  whether everybody does. */
export function contestOf(row: MatrixRow): Contest {
    const them = PRODUCTS.some(product => ANSWERS(row.them[product]));
    const us = ANSWERS(row.ours);
    if (them && us) return "contested";
    if (them && !us) return row.ours.stance === "declined" ? "ceded" : "behind";
    if (!them && us) return "ahead";
    return "neither";
}

export type MatrixSection = {title: string; blurb: string; rows: MatrixRow[]};

export const SOURCES = [
    {product: "All six", when: "31 October 2026",
     how: "Desk research against vendor help centres (PTC, Siemens, Oracle, Duro, OpenBOM), API documentation and, where nothing else was readable, vendor marketing; one source per claim is recorded with the research. No trial accounts, no uploads, no source code."},
    {product: "ParkourAI · QMS", when: "31 October 2026",
     how: "What is built and deployed, read from the product's own developer notes and guides; nothing planned is counted."},
];

const yes = (note?: string): Cell => ({level: "yes", note});
const partial = (note?: string): Cell => ({level: "partial", note});
const no = (note?: string): Cell => ({level: "no", note});
const unknown = (note?: string): Cell => ({level: "unknown", note});

export const MATRIX: MatrixSection[] = [
    {
        title: "Structure and navigation",
        blurb: "The bill itself: what a line carries, and the two walks every reader needs.",
        rows: [
            {capability: "Multi-level structure, with a quantity and a unit on every line",
             them: {Arena: yes(), Windchill: yes("Quantity and a unit from a fixed list on each usage link."), Teamcenter: yes(), "Agile PLM": yes(), Duro: yes(), OpenBOM: yes()},
             ours: yes("A line joins two Items; quantity above zero in a unit from the tenant's list.")},
            {capability: "Multi-level explosion with extended quantities",
             note: "An indented bill where each line's quantity is multiplied by the path above it.",
             them: {Arena: partial("A multi-level view; extended quantities not documented (marketing only)."), Windchill: yes("Multi-Level BOM report and roll-up report."), Teamcenter: partial("Named on the product page; not documented."), "Agile PLM": yes("BOM Explosion report to a chosen depth."), Duro: partial("A flattened BOM; extended quantities not described."), OpenBOM: yes("Flattened BOM rolls quantities up.")},
             ours: yes("To twenty levels, extended quantity and extended cost per line, each node under the reader's persona.")},
            {capability: "Where-used, multi-level",
             them: {Arena: yes(), Windchill: yes(), Teamcenter: yes(), "Agile PLM": yes(), Duro: yes("One level, the assemblies a component is used in."), OpenBOM: yes()},
             ours: yes("Every parent and its parents in turn, under the reader's persona.")},
            {capability: "Reference designators on a line",
             them: {Arena: yes(), Windchill: yes("Ranges and lists accepted; the quantity is raised to match."), Teamcenter: yes("From a third-party training site."), "Agile PLM": yes("Ranges, a count-versus-quantity warning, a duplicate rule."), Duro: yes(), OpenBOM: partial("A configurable property; no validation.")},
             ours: yes("Lists and ranges expanded, several positions must match the quantity, no position twice on a line or across the bill; refused at the write by code.")},
            {capability: "Quantity roll-up across levels",
             note: "One row per distinct component with what one of the item needs over every path.",
             them: {Arena: unknown(), Windchill: yes("Multi-Level Components List."), Teamcenter: partial("'Advanced BOM Rollup' is named, not described."), "Agile PLM": yes("Consolidated BOM report."), Duro: partial("A flattened BOM; the aggregation rule is undocumented."), OpenBOM: yes()},
             ours: yes("In the dimension's base unit, with a component whose unit does not convert said so rather than left out.")},
        ],
    },
    {
        title: "Units and cost",
        blurb: "Where a line in kilograms meets a line in grams, and what one of the item costs.",
        rows: [
            {capability: "Unit of measure on a line, with conversion between units",
             them: {Arena: partial("A unit column; no conversion."), Windchill: partial("Attributes are stored in SI base units; no conversion between usage units is documented."), Teamcenter: unknown(), "Agile PLM": unknown("The documented BOM fields carry no unit."), Duro: partial("A unit field that maps to the ERP's; no conversion."), OpenBOM: partial("A property; conversion by a user-built catalogue formula.")},
             ours: yes("A base unit per dimension with exact platform factors; a tenant declares its own units and factors; conversion only within a dimension.")},
            {capability: "Standard cost per line and a cost roll-up",
             them: {Arena: partial("'Calculate the cost of an assembly'; the roll-up is not documented."), Windchill: partial("A separate, older Windchill Cost module."), Teamcenter: partial("A separate product, Product Cost Management."), "Agile PLM": partial("A separately licensed module."), Duro: yes("Primary-source unit price rolled up on the component page."), OpenBOM: yes("Formula roll-ups up each level.")},
             ours: yes("Standard cost and currency per line, extended by the path; a total only when every line is costed in one currency and nothing is hidden.")},
        ],
    },
    {
        title: "Configuration and effectivity",
        blurb: "Variants of one bill, and which revision of a component a line means.",
        rows: [
            {capability: "Configurations, variants, alternates and substitutes",
             them: {Arena: partial("Line-level substitutes mentioned in marketing; not documented."), Windchill: yes("Options and variants, alternates and substitutes per structure."), Teamcenter: yes("Substitutes on lines; variant configuration."), "Agile PLM": partial("Option BOMs; alternates through the AML; no line-level substitute."), Duro: partial("Alternates and variants as items; no line-level substitute."), OpenBOM: partial("Modelled by convention, not a feature.")},
             ours: yes("Named configurations of one item's bill with one default, and a substitute on a line: same parent, a different part, counted in nothing until chosen.")},
            {capability: "A line pinned to one revision, or floating to the current one",
             them: {Arena: partial("Floating; history reconstructs a past build."), Windchill: yes("Configuration specifications: latest, baseline, date and unit effectivity."), Teamcenter: yes("Precise and imprecise structures, revision rules."), "Agile PLM": partial("Floating only."), Duro: partial("Effectivity dates on a change order; the child's revision is not documented."), OpenBOM: partial("Immutable revision snapshots; the rule has changed between posts.")},
             ours: yes("Per line: pinned to one revision of the component or following the current one; no date or unit effectivity.")},
        ],
    },
    {
        title: "Control and validation",
        blurb: "What stops a released definition drifting, and what stops a bill being wrong by construction.",
        rows: [
            {capability: "Change control on a released bill",
             note: "A change to a released bill needs a change order, and the bill is bound to the item's revision.",
             them: {Arena: yes("ECOs carry BOM redlines."), Windchill: yes("Change notices with BOM redlines; enforcement by access rules."), Teamcenter: yes("Edits in a change-notice context."), "Agile PLM": yes("A released bill is edited only through an ECO."), Duro: yes("Change orders required in prototype and production status."), OpenBOM: partial("Optional change management.")},
             ours: yes("A change to a released bill needs an open change order under the tenant's policy, the change order is written on the item's history, and releasing an item captures its bill against the revision with a fingerprint; the bill is captured rather than bound to the revision, by decision.")},
            {capability: "Approved manufacturer or vendor list on an item",
             them: {Arena: yes("AML and AVL."), Windchill: yes("AML and AVL, with a Supplier Management module."), Teamcenter: partial("Vendor and commercial parts; preferred status per context is an open question."), "Agile PLM": yes("Manufacturers tab with preferred status."), Duro: yes("Manufacturer and primary source per component."), OpenBOM: partial("A vendor list; no approval status.")},
             ours: yes("An approved supplier list on the supplier's record, read on the item, with ASL status and one primary, and the manufacturer and its part number on each row when the supplier is a distributor.")},
            {capability: "Cycle prevention and structure validation",
             them: {Arena: unknown(), Windchill: partial("A reflexive-link error reported by users; no help page."), Teamcenter: unknown(), "Agile PLM": yes("A 'BOM Multi-Level Recursion' SmartRule."), Duro: unknown("Only an obsolete-child check was found."), OpenBOM: unknown()},
             ours: yes("A line that would make a bill contain itself is refused before it exists, with the loop named; two Items only; a quantity above zero in a known unit.")},
            {capability: "Comparison or redline between two revisions of a bill",
             them: {Arena: yes(), Windchill: yes(), Teamcenter: yes("From a third-party training site."), "Agile PLM": yes(), Duro: partial("Revision history per component; compare only on a marketing page."), OpenBOM: yes()},
             ours: yes("Two captures of a bill, or a capture and the bill as it stands, line by line: Added, Removed, Changed with each field's value before and after, a line identified by where it sits. On the tab and on /t3/v1/bom_compare.")},
        ],
    },
    {
        title: "Getting bills in and out",
        blurb: "Where a bill comes from and how a pipeline reads it.",
        rows: [
            {capability: "Import and export: spreadsheet, CAD",
             them: {Arena: yes(), Windchill: yes(), Teamcenter: yes(), "Agile PLM": yes(), Duro: yes(), OpenBOM: yes()},
             ours: yes("A CSV or pasted rows imported on the tab or through /t3/v1/bom_import, naming components by ID and configurations by name, every row through the line rules and answered on its own; the explosion exported as a CSV that imports as is. No CAD reader.")},
            {capability: "API access to the bill",
             them: {Arena: yes("REST, including line create and update."), Windchill: yes("REST and OData."), Teamcenter: yes("SOA services and ITK."), "Agile PLM": yes("SDK and web services."), Duro: yes("GraphQL."), OpenBOM: yes("REST, including the flattened bill.")},
             ours: yes("The explosion, where-used, configurations and units for read, and a line written in its own words (POST, PUT, DELETE /t3/v1/bom_lines) under the same rules as the screens, all under the Item entity.")},
        ],
    },
];

/** Section titles to leave off the page; "*" hides the matrix entirely. A list rather than a
 *  deletion, so hiding is reversible and the data stays under test. */
export const HIDDEN_SECTIONS: string[] = [];

export const VISIBLE_MATRIX = () =>
    HIDDEN_SECTIONS.includes("*") ? [] : MATRIX.filter(section => !HIDDEN_SECTIONS.includes(section.title));
